import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
    Mail, Eye, RotateCcw, Save, Send, Loader2, CheckCircle2, AlertCircle,
    FileCode2, Languages,
} from "lucide-react";

/**
 * Super Admin — Email Templates CMS.
 * Left column: list of known templates (name, description, override badge).
 * Right column: editor with tabs for EN/SQ + subject/HTML/text + logo,
 * plus a live preview panel and Send-Test action.
 */
export default function SuperEmailTemplates() {
    const { t } = useTranslation();
    const [templates, setTemplates] = useState([]);
    const [selectedKey, setSelectedKey] = useState(null);
    const [detail, setDetail] = useState(null);
    const [lang, setLang] = useState("en");
    const [form, setForm] = useState({});
    const [preview, setPreview] = useState(null);
    const [saving, setSaving] = useState(false);
    const [previewing, setPreviewing] = useState(false);
    const [testEmail, setTestEmail] = useState("");

    const loadList = async () => {
        const { data } = await api.get("/superadmin/email-templates");
        setTemplates(data);
        if (!selectedKey && data.length) setSelectedKey(data[0].key);
    };

    const loadDetail = async (key) => {
        const { data } = await api.get(`/superadmin/email-templates/${key}`);
        setDetail(data);
        // Seed form with the current effective values
        const eff = data.effective || {};
        setForm({
            subject_en: eff.subject_en || "",
            subject_sq: eff.subject_sq || "",
            html_en: eff.html_en || "",
            html_sq: eff.html_sq || "",
            text_en: eff.text_en || "",
            text_sq: eff.text_sq || "",
            logo_text: eff.logo_text || "akomoda",
            logo_url: eff.logo_url || "",
        });
        setPreview(null);
    };

    useEffect(() => { loadList(); }, []);
    useEffect(() => { if (selectedKey) loadDetail(selectedKey); }, [selectedKey]);

    const doPreview = async () => {
        if (!selectedKey) return;
        setPreviewing(true);
        try {
            // Save first if the user has unsaved changes? For simplicity we
            // just render whatever the DB has right now — the admin can Save
            // then preview. To let them preview without saving we send the
            // in-flight form values via a temp override.
            const { data } = await api.post(`/superadmin/email-templates/${selectedKey}/preview`, {
                lang, variables: {},
            });
            setPreview(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Preview failed");
        } finally { setPreviewing(false); }
    };

    const doSave = async () => {
        if (!selectedKey) return;
        setSaving(true);
        try {
            await api.put(`/superadmin/email-templates/${selectedKey}`, form);
            toast.success("Template saved");
            await loadList();
            await loadDetail(selectedKey);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
        } finally { setSaving(false); }
    };

    const doReset = async () => {
        if (!selectedKey) return;
        if (!window.confirm("Revert this template to the built-in default? Your custom copy will be discarded.")) return;
        try {
            await api.post(`/superadmin/email-templates/${selectedKey}/reset`);
            toast.success("Reverted to default");
            await loadList();
            await loadDetail(selectedKey);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    const sendTest = async () => {
        if (!selectedKey || !testEmail) return;
        try {
            await api.post(`/superadmin/email-templates/${selectedKey}/test-send`, {
                lang, to: testEmail.trim(), variables: {},
            });
            toast.success(`Test email sent to ${testEmail}`);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
        }
    };

    const active = templates.find((t) => t.key === selectedKey);

    return (
        <>
            <Topbar title={t("super_admin.email_templates_title")} subtitle="" />
            <div className="flex-1 overflow-auto p-6" data-testid="super-email-templates">
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-[280px,minmax(0,1fr)]">
                    {/* Sidebar list */}
                    <div className="akm-card p-2">
                        <ul className="flex flex-col gap-0.5">
                            {templates.map((t) => (
                                <li key={t.key}>
                                    <button
                                        onClick={() => setSelectedKey(t.key)}
                                        data-testid={`et-select-${t.key}`}
                                        className={`w-full rounded-md px-3 py-2.5 text-left transition ${
                                            selectedKey === t.key
                                                ? "bg-orange-50 text-[#F26322]"
                                                : "text-slate-700 hover:bg-slate-50"
                                        }`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <Mail size={14} className={selectedKey === t.key ? "text-[#F26322]" : "text-slate-400"} />
                                            <div className="min-w-0 flex-1">
                                                <div className="truncate text-[13px] font-semibold">{t.name}</div>
                                                <div className="truncate text-[11px] text-slate-500">{t.key}</div>
                                            </div>
                                            {t.is_overridden && (
                                                <span className="rounded-full bg-emerald-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-700">
                                                    Custom
                                                </span>
                                            )}
                                        </div>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Editor + preview */}
                    <div className="space-y-4">
                        {active && detail ? (
                            <>
                                <div className="akm-card p-5">
                                    <div className="flex flex-wrap items-start justify-between gap-3">
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h2 className="text-[17px] font-semibold text-foreground">{active.name}</h2>
                                                {detail.is_overridden ? (
                                                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                                                        <CheckCircle2 size={10} /> Overridden
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-600">
                                                        Using default
                                                    </span>
                                                )}
                                            </div>
                                            <p className="mt-1 text-[13px] text-muted-foreground">{active.description}</p>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            <button
                                                onClick={doReset}
                                                data-testid="et-reset"
                                                disabled={!detail.is_overridden}
                                                className="akm-btn-ghost h-9 text-[12.5px] disabled:opacity-40"
                                            >
                                                <RotateCcw size={13} /> Reset to default
                                            </button>
                                            <button
                                                onClick={doPreview}
                                                data-testid="et-preview"
                                                disabled={previewing}
                                                className="akm-btn-ghost h-9 text-[12.5px] disabled:opacity-50"
                                            >
                                                {previewing ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />} Preview
                                            </button>
                                            <button
                                                onClick={doSave}
                                                data-testid="et-save"
                                                disabled={saving}
                                                className="akm-btn-primary h-9 text-[12.5px] disabled:opacity-50"
                                            >
                                                {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save changes
                                            </button>
                                        </div>
                                    </div>

                                    {/* Language selector */}
                                    <div className="mt-4 inline-flex rounded-xl border border-slate-200 bg-slate-50 p-1">
                                        {[{ v: "en", l: "English" }, { v: "sq", l: "Shqip" }].map((op) => (
                                            <button
                                                key={op.v}
                                                onClick={() => setLang(op.v)}
                                                data-testid={`et-lang-${op.v}`}
                                                className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12px] font-semibold transition ${
                                                    lang === op.v ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"
                                                }`}
                                            >
                                                <Languages size={12} /> {op.l}
                                            </button>
                                        ))}
                                    </div>

                                    {/* Editable fields */}
                                    <div className="mt-4 space-y-4">
                                        <Field label="Subject">
                                            <input
                                                data-testid={`et-subject-${lang}`}
                                                className="akm-input"
                                                value={form[`subject_${lang}`] || ""}
                                                onChange={(e) => setForm({ ...form, [`subject_${lang}`]: e.target.value })}
                                            />
                                        </Field>
                                        <div className="grid gap-4 md:grid-cols-2">
                                            <Field label="Logo text (fallback)">
                                                <input
                                                    data-testid="et-logo-text"
                                                    className="akm-input"
                                                    value={form.logo_text || ""}
                                                    onChange={(e) => setForm({ ...form, logo_text: e.target.value })}
                                                />
                                            </Field>
                                            <Field label="Logo URL (optional)">
                                                <input
                                                    data-testid="et-logo-url"
                                                    className="akm-input"
                                                    placeholder="https://cdn.akomoda.co/logo.png"
                                                    value={form.logo_url || ""}
                                                    onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                                                />
                                            </Field>
                                        </div>
                                        <Field label={<>HTML body <span className="text-[10px] text-slate-400">— variables: {(active.variables || []).map((v) => `{{${v}}}`).join(", ")}</span></>}>
                                            <textarea
                                                data-testid={`et-html-${lang}`}
                                                className="akm-input min-h-[260px] font-mono text-[12px]"
                                                value={form[`html_${lang}`] || ""}
                                                onChange={(e) => setForm({ ...form, [`html_${lang}`]: e.target.value })}
                                            />
                                        </Field>
                                        <Field label="Plain-text body (fallback for text-only clients)">
                                            <textarea
                                                data-testid={`et-text-${lang}`}
                                                className="akm-input min-h-[110px] font-mono text-[12px]"
                                                value={form[`text_${lang}`] || ""}
                                                onChange={(e) => setForm({ ...form, [`text_${lang}`]: e.target.value })}
                                            />
                                        </Field>
                                    </div>

                                    {/* Send test */}
                                    <div className="mt-5 flex flex-wrap items-end gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
                                        <div className="flex-1 min-w-[220px]">
                                            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Send test email</div>
                                            <input
                                                data-testid="et-test-email"
                                                type="email"
                                                placeholder="you@example.com"
                                                className="akm-input mt-1"
                                                value={testEmail}
                                                onChange={(e) => setTestEmail(e.target.value)}
                                            />
                                        </div>
                                        <button
                                            onClick={sendTest}
                                            data-testid="et-send-test"
                                            disabled={!testEmail}
                                            className="akm-btn-primary h-10 disabled:opacity-40"
                                        >
                                            <Send size={13} /> Send test
                                        </button>
                                    </div>
                                </div>

                                {/* Preview */}
                                {preview && (
                                    <div className="akm-card p-5" data-testid="et-preview-panel">
                                        <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
                                            <FileCode2 size={14} className="text-[#F26322]" />
                                            <div className="text-[13px] font-semibold">Preview</div>
                                            <span className="ml-auto text-[11px] text-muted-foreground">{preview.subject}</span>
                                        </div>
                                        <div className="mt-3 overflow-auto rounded-lg border border-slate-200 bg-white">
                                            <iframe
                                                title="preview"
                                                srcDoc={preview.html}
                                                className="h-[520px] w-full"
                                                sandbox=""
                                            />
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="akm-card p-6 text-center text-sm text-muted-foreground">
                                <AlertCircle size={16} className="mx-auto mb-2 text-slate-400" />
                                Select a template to edit
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
            {children}
        </label>
    );
}
